/**
 * State vocabularies for :func:`StateBand` (REQ-STATUS-070, REQ-STATUS-075).
 *
 * # Why this module exists
 *
 * ``StateBand`` was a **rollup** mark wearing a generic name: its weight table
 * was keyed on `healthy` / `standby` / `degraded` / `unhealthy` / `unknown`, so
 * anything else fell through to the `unknown` weight and rendered as a row of
 * identical bars. The trend detail chart needs the same *shape* for a
 * ``boolean`` metric — a boolean over time is a **state**, and a line between
 * `true` and `false` implies an interpolation that does not exist — so the
 * vocabulary became a parameter.
 *
 * They live in their own module rather than beside the component because they
 * are data, not components: exporting them from the `.tsx` costs Fast Refresh
 * on every file that imports it.
 *
 * Cross-references:
 *   - REQ-UI-046 — state is never encoded by colour alone; **height** is the
 *     non-colour channel here, so two states that must be told apart may not
 *     share a weight.
 *   - REQ-STATUS-073 — an absent sample is absent; ``gap`` draws nothing.
 *   - `design-system.md` → Component patterns → Trend detail chart.
 */
import { rollupStateMeta, type RollupState } from "@/system-status/status-model";

/** How one band renders a vocabulary of state tokens. */
export interface BandVocabulary {
  /**
   * Bar height as a fraction of the band's height, per state token. This is
   * the **non-colour channel** (REQ-UI-046), so two states that must be told
   * apart may not share a weight. ``0`` draws nothing at all.
   */
  weight: Readonly<Record<string, number>>;
  /** SVG ``fill`` for a state token. */
  fill: (state: string) => string;
  /** The token an unrecognized state resolves to. Must be a *visible* one. */
  fallback: string;
}

/**
 * The rollup-state vocabulary — a worse state draws a taller bar.
 *
 * Unchanged from the pre-2026-08-03 table, and still the default, so every
 * existing caller renders exactly as it did.
 */
export const ROLLUP_BAND: BandVocabulary = {
  weight: { healthy: 0.35, standby: 0.5, degraded: 0.7, unhealthy: 1, unknown: 0.85 },
  fill: (state) => rollupStateMeta(state as RollupState).dotColor,
  fallback: "unknown",
};

/**
 * The ``boolean`` vocabulary for the trend detail chart (REQ-STATUS-075).
 *
 * ⚠ **Deliberately not the status ramp.** A boolean is not a health judgement:
 * ``lease_active = false`` on a standby instance is *correct*, not bad, and
 * painting it red would be a false alarm on the surface whose governing rule is
 * never a false reading (REQ-STATUS-016). The colours are the brand line token
 * and a recessive decoration token; the meaning is carried by **height**, which
 * survives being read without colour at all.
 *
 * ``gap`` draws nothing — an absent sample is absent, and a short bar would
 * read as a measured `no` (REQ-STATUS-073). ``unknown`` is the **loud**
 * fallback: a token this vocabulary does not know must be *visible* as "we do
 * not know", because silence already means "no sample".
 */
export const BOOLEAN_BAND: BandVocabulary = {
  weight: { yes: 1, no: 0.35, gap: 0, unknown: 0.85 },
  fill: (state) =>
    state === "yes"
      ? "hsl(var(--ring))"
      : state === "no"
        ? "hsl(var(--decor-muted))"
        : state === "gap"
          ? "none"
          : "var(--status-unknown)",
  fallback: "unknown",
};
